// A visible insert/format toolbar for the doc editor, so block types aren't hidden
// behind "/". Blocks insert at the cursor (best-effort; falls back to end of doc).
import React, { useState } from 'react';
import { viewPresets } from '@blocksuite/data-view/view-presets';
import { Text } from '@blocksuite/store';
import { Icon } from './icons';

// AffineEditorContainer at runtime (exposes .doc and .std). Loosely typed on purpose.
type Editor = any;

function noteOf(editor: Editor): { doc: any; note: any } {
  const doc = editor?.doc;
  const note = doc?.root?.children?.find((b: any) => b.flavour === 'affine:note');
  return { doc, note };
}

/** The block the cursor is in (best-effort across selection shapes), or null. */
function focusedModel(editor: Editor): any {
  try {
    const sels = editor?.std?.selection?.value ?? [];
    for (const s of sels) {
      const id = s.blockId ?? s.from?.blockId ?? (Array.isArray(s.path) ? s.path[s.path.length - 1] : undefined);
      if (id) {
        const model = editor.doc.getBlock?.(id)?.model;
        if (model) return model;
      }
    }
  } catch {
    /* fall through to append */
  }
  return null;
}

function addSimple(editor: Editor, flavour: string, props: Record<string, unknown>): void {
  const { doc, note } = noteOf(editor);
  if (!doc || !note) return;
  const cur = focusedModel(editor);
  if (cur) {
    const parent = doc.getParent?.(cur) ?? note;
    doc.addBlock(flavour, props, parent.id, parent.children.indexOf(cur) + 1);
  } else {
    doc.addBlock(flavour, props, note.id);
  }
}

// Mirrors insertDatabaseBlockCommand: sibling database next to the cursor block, then
// init its columns + the chosen view (table or kanban) via the database service.
function addDatabase(editor: Editor, viewType: string): void {
  const { doc, note } = noteOf(editor);
  if (!doc || !note) return;
  const target = focusedModel(editor) ?? (note.children?.length ? note.children[note.children.length - 1] : note);
  const service = editor.std?.getService?.('affine:database');
  if (!service || !target) return;
  const ids = doc.addSiblingBlocks(target, [{ flavour: 'affine:database' }], 'after');
  if (ids?.length) service.initDatabaseBlock(doc, target, ids[0], viewType, false);
}

// Embed/equation input goes through the in-app modal (see DocInsertBar state):
// window.prompt is suppressed by browsers after the first dismissal, which made
// consecutive embed inserts silently fail.

function addImage(editor: Editor): void {
  const { doc, note } = noteOf(editor);
  if (!doc || !note) return;
  const input = document.createElement('input');
  input.type = 'file';
  input.accept = 'image/*';
  input.onchange = async () => {
    const file = input.files?.[0];
    if (!file) return;
    try {
      const blobs = doc.blobSync ?? doc.collection?.blobSync;
      const sourceId = await blobs?.set(file);
      if (sourceId) doc.addBlock('affine:image', { sourceId }, note.id);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('[docs] image insert failed', e);
    }
  };
  input.click();
}

function addAttachment(editor: Editor): void {
  const { doc, note } = noteOf(editor);
  if (!doc || !note) return;
  const input = document.createElement('input');
  input.type = 'file';
  input.onchange = async () => {
    const file = input.files?.[0];
    if (!file) return;
    try {
      const blobs = doc.blobSync ?? doc.collection?.blobSync;
      const sourceId = await blobs?.set(file);
      if (sourceId) {
        doc.addBlock('affine:attachment', {
          sourceId,
          name: file.name,
          size: file.size,
          type: file.type,
        }, note.id);
      }
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('[docs] attachment insert failed', e);
    }
  };
  input.click();
}

function formatInline(editor: Editor, key: string, mod: string): void {
  const el = (editor as HTMLElement)?.querySelector?.('[contenteditable]') as HTMLElement | null;
  if (!el) return;
  el.focus();
  const evt = new KeyboardEvent('keydown', { key, bubbles: true, cancelable: true, ctrlKey: mod === 'ctrl', metaKey: mod === 'meta' });
  el.dispatchEvent(evt);
}

function triggerFormat(editor: Editor, key: string): void {
  const isMac = /Mac|iPod|iPhone|iPad/.test(navigator.platform);
  formatInline(editor, key, isMac ? 'meta' : 'ctrl');
}

function insertDateText(editor: Editor, dateStr: string): void {
  addSimple(editor, 'affine:paragraph', { text: new Text(dateStr) });
}

function moveBlock(editor: Editor, dir: 'up' | 'down'): void {
  const { doc } = noteOf(editor);
  if (!doc) return;
  const cur = focusedModel(editor);
  if (!cur) return;
  const parent = doc.getParent?.(cur);
  if (!parent) return;
  const idx = parent.children.indexOf(cur);
  if (dir === 'up' && idx > 0) doc.moveBlocks([cur], parent, parent.children[idx - 1], true);
  else if (dir === 'down' && idx < parent.children.length - 1) doc.moveBlocks([cur], parent, parent.children[idx + 1], false);
}

function duplicateBlock(editor: Editor): void {
  const { doc, note } = noteOf(editor);
  if (!doc || !note) return;
  const cur = focusedModel(editor);
  if (!cur) return;
  const parent = doc.getParent?.(cur) ?? note;
  const idx = parent.children.indexOf(cur);
  const props: Record<string, unknown> = {};
  if (cur.text) {
    try { props.text = cur.text.clone(); } catch { props.text = new Text(cur.text.toString()); }
  }
  if (cur.type) props.type = cur.type;
  doc.addBlock(cur.flavour, props, parent.id, idx + 1);
}

function deleteBlock(editor: Editor): void {
  const { doc } = noteOf(editor);
  if (!doc) return;
  const cur = focusedModel(editor);
  if (!cur) return;
  doc.deleteBlock(cur);
}

function copyBlock(editor: Editor): void {
  const cur = focusedModel(editor);
  if (!cur) return;
  const text = cur.text?.toString?.() || '';
  if (text) navigator.clipboard.writeText(text).catch(() => {});
}

function todayStr(): string {
  return new Date().toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });
}
function tomorrowStr(): string {
  const d = new Date(); d.setDate(d.getDate() + 1);
  return d.toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });
}
function yesterdayStr(): string {
  const d = new Date(); d.setDate(d.getDate() - 1);
  return d.toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });
}
function nowStr(): string {
  return new Date().toLocaleString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit' });
}

function Btn({ title, onClick, children }: {
  title: string; onClick: (e: React.MouseEvent) => void; children: React.ReactNode;
}): React.ReactElement {
  return (
    <button type="button" className="fx-docbar-btn" title={title} aria-label={title}
      onMouseDown={(e) => e.preventDefault()} onClick={onClick}>
      {children}
    </button>
  );
}

export function DocInsertBar({ getEditor, onCropImage, onNewDoc, onLinkedDoc }: {
  getEditor: () => Editor;
  onCropImage: () => void;
  onNewDoc?: () => void;
  onLinkedDoc?: () => void;
}): React.ReactElement {
  const [ask, setAsk] = useState<{ label: string; cb: (v: string) => void } | null>(null);
  const [askValue, setAskValue] = useState('');
  const run = (fn: (e: Editor) => void) => (ev: React.MouseEvent) => {
    ev.preventDefault();
    const editor = getEditor();
    if (!editor) return;
    try { fn(editor); } catch (e) {
      // eslint-disable-next-line no-console
      console.error('[docs] insert failed', e);
    }
  };
  const prompt = (label: string, cb: (v: string) => void) => {
    setAskValue('');
    setAsk({ label, cb });
  };
  const askSubmit = () => {
    const v = askValue.trim();
    if (ask && v) {
      try { ask.cb(v); } catch (e) {
        // eslint-disable-next-line no-console
        console.error('[docs] insert failed', e);
      }
    }
    setAsk(null);
  };
  const addEmbed = (editor: Editor, flavour: string, label: string) =>
    prompt(label, (url) => addSimple(editor, flavour, { url }));
  return (
    <div className="fx-docbar">
      {/* ── Inline formatting ── */}
      <div className="fx-docbar-grp">
        <Btn title="Bold (Ctrl+B)" onClick={run((e) => triggerFormat(e, 'b'))}><b>B</b></Btn>
        <Btn title="Italic (Ctrl+I)" onClick={run((e) => triggerFormat(e, 'i'))}><i>I</i></Btn>
        <Btn title="Underline (Ctrl+U)" onClick={run((e) => triggerFormat(e, 'u'))}><u>U</u></Btn>
        <Btn title="Strikethrough (Ctrl+Shift+S)" onClick={run((e) => triggerFormat(e, 's'))}><s>S</s></Btn>
      </div>
      {/* ── Headings ── */}
      <div className="fx-docbar-grp">
        <Btn title="Heading 1" onClick={run((e) => addSimple(e, 'affine:paragraph', { type: 'h1' }))}>H1</Btn>
        <Btn title="Heading 2" onClick={run((e) => addSimple(e, 'affine:paragraph', { type: 'h2' }))}>H2</Btn>
        <Btn title="Heading 3" onClick={run((e) => addSimple(e, 'affine:paragraph', { type: 'h3' }))}>H3</Btn>
        <Btn title="Heading 4" onClick={run((e) => addSimple(e, 'affine:paragraph', { type: 'h4' }))}>H4</Btn>
        <Btn title="Heading 5" onClick={run((e) => addSimple(e, 'affine:paragraph', { type: 'h5' }))}>H5</Btn>
        <Btn title="Heading 6" onClick={run((e) => addSimple(e, 'affine:paragraph', { type: 'h6' }))}>H6</Btn>
      </div>
      {/* ── Lists ── */}
      <div className="fx-docbar-grp">
        <Btn title="Bulleted list" onClick={run((e) => addSimple(e, 'affine:list', { type: 'bulleted' }))}><Icon name="listUl" size={15} /></Btn>
        <Btn title="Numbered list" onClick={run((e) => addSimple(e, 'affine:list', { type: 'numbered' }))}><Icon name="listOl" size={15} /></Btn>
        <Btn title="To-do list" onClick={run((e) => addSimple(e, 'affine:list', { type: 'todo' }))}><Icon name="markread" size={15} /></Btn>
      </div>
      {/* ── Blocks ── */}
      <div className="fx-docbar-grp">
        <Btn title="Quote" onClick={run((e) => addSimple(e, 'affine:paragraph', { type: 'quote' }))}><Icon name="quote" size={15} /></Btn>
        <Btn title="Code block" onClick={run((e) => addSimple(e, 'affine:code', {}))}><Icon name="codeblock" size={15} /></Btn>
        <Btn title="Divider" onClick={run((e) => addSimple(e, 'affine:divider', {}))}><span className="fx-docbar-dash">—</span></Btn>
        <Btn title="Equation block (LaTeX)" onClick={run((e) => addSimple(e, 'affine:latex', {}))}>∑</Btn>
        <Btn title="Inline equation" onClick={run((e) => {
          const { doc, note } = noteOf(e);
          if (!doc || !note) return;
          prompt('LaTeX expression (e.g. E=mc^2)', (latex) => addSimple(e, 'affine:paragraph', { text: new Text(latex) }));
        })}>𝑥²</Btn>
      </div>
      {/* ── Data views ── */}
      <div className="fx-docbar-grp">
        <Btn title="Table" onClick={run((e) => addDatabase(e, viewPresets.tableViewMeta.type))}><Icon name="grid" size={15} /> Table</Btn>
        <Btn title="Kanban board" onClick={run((e) => addDatabase(e, viewPresets.kanbanViewMeta.type))}><Icon name="apps" size={15} /> Kanban</Btn>
      </div>
      {/* ── Media & embeds ── */}
      <div className="fx-docbar-grp">
        <Btn title="Image" onClick={run((e) => addImage(e))}><Icon name="files" size={15} /></Btn>
        <Btn title="Attachment" onClick={run((e) => addAttachment(e))}><Icon name="paperclip" size={15} /></Btn>
        <Btn title="Link / bookmark" onClick={run((e) => addEmbed(e, 'affine:bookmark', 'Paste a link URL'))}><Icon name="link" size={15} /></Btn>
        <Btn title="YouTube" onClick={run((e) => addEmbed(e, 'affine:embed-youtube', 'Paste a YouTube URL'))}><Icon name="video" size={15} /></Btn>
        <Btn title="GitHub" onClick={run((e) => addEmbed(e, 'affine:embed-github', 'Paste a GitHub URL (repo, issue, or PR)'))}>GH</Btn>
        <Btn title="Figma" onClick={run((e) => addEmbed(e, 'affine:embed-figma', 'Paste a Figma URL'))}>Fig</Btn>
        <Btn title="Loom" onClick={run((e) => addEmbed(e, 'affine:embed-loom', 'Paste a Loom URL'))}>Loom</Btn>
      </div>
      {/* ── Docs ── */}
      <div className="fx-docbar-grp">
        {onNewDoc && <Btn title="New doc" onClick={(e) => { e.preventDefault(); onNewDoc(); }}><Icon name="plus" size={15} /> Doc</Btn>}
        {onLinkedDoc && <Btn title="Linked doc" onClick={(e) => { e.preventDefault(); onLinkedDoc(); }}><Icon name="link" size={15} /> Linked</Btn>}
      </div>
      {/* ── Dates ── */}
      <div className="fx-docbar-grp">
        <Btn title="Insert today's date" onClick={run((e) => insertDateText(e, todayStr()))}>Today</Btn>
        <Btn title="Insert tomorrow's date" onClick={run((e) => insertDateText(e, tomorrowStr()))}>Tomorrow</Btn>
        <Btn title="Insert yesterday's date" onClick={run((e) => insertDateText(e, yesterdayStr()))}>Yesterday</Btn>
        <Btn title="Insert current date & time" onClick={run((e) => insertDateText(e, nowStr()))}>Now</Btn>
      </div>
      {/* ── Block operations ── */}
      <div className="fx-docbar-grp">
        <Btn title="Move block up" onClick={run((e) => moveBlock(e, 'up'))}>↑</Btn>
        <Btn title="Move block down" onClick={run((e) => moveBlock(e, 'down'))}>↓</Btn>
        <Btn title="Copy block text" onClick={run((e) => copyBlock(e))}>Copy</Btn>
        <Btn title="Duplicate block" onClick={run((e) => duplicateBlock(e))}>Dup</Btn>
        <Btn title="Delete block" onClick={run((e) => deleteBlock(e))}><Icon name="trash" size={15} /></Btn>
      </div>
      {/* ── Image tools ── */}
      <div className="fx-docbar-grp">
        <Btn title="Crop the selected image" onClick={(e) => { e.preventDefault(); onCropImage(); }}><Icon name="screen" size={15} /> Crop</Btn>
      </div>

      {ask && (
        <div className="fx-modal-backdrop" onClick={() => setAsk(null)}>
          <div className="fx-modal" onClick={(e) => e.stopPropagation()}>
            <div className="fx-login-title">{ask.label}</div>
            <input className="fx-text-input fx-login-input" autoFocus value={askValue}
              onChange={(e) => setAskValue(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') { e.preventDefault(); askSubmit(); }
                if (e.key === 'Escape') setAsk(null);
              }} />
            <div className="fx-modal-actions">
              <button type="button" className="fx-login-back" onClick={() => setAsk(null)}>Cancel</button>
              <button type="button" className="fx-sendbtn" onClick={askSubmit} disabled={!askValue.trim()}>Insert</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
